// Process templates (/processtemplates) — editor model, labels and client-side hints.
// Mirrors AveOrga.Shared.Contracts.Processes.ProcessTemplateContracts and the ProcessStepValidator /
// ProcessTemplateService.ValidateAsync rules. The hints here only guide the admin while editing;
// POST /ProcessTemplate/Validate stays authoritative (it runs before every save).
import { normalizeColorKey, normalizeRole, normalizeSignupChannel } from '../../util/participantGroups';
import { parsePriceInput, toPriceInput } from '../../util/eventPrices';
import { flatRateRowsIssue } from '../flat-rate/FlatRateRowsEditor';
import {
  ADD_ON_ROW_MODE, addOnRowsIssue, addOnRowsToTemplateRequest, templateAddOnToRow,
} from '../add-on/AddOnRowsEditor';
import { FLAT_RATE_KIND, isDeduction, normalizeFlatRateKind } from '../../util/flatRates';
import {
  NEWSLETTER_TARGET_LABEL, STEP_KIND_ICON, STEP_KIND_LABEL, TEMPLATE_RULES,
} from '../../util/processSteps';

// ── Enums (strings via JsonStringEnumConverter; numbers tolerated) ──────────────────────────────
export const STEP_KINDS = ['AssignLeaders', 'Release', 'SendMail', 'ManualCheck'];
const TARGET_KINDS = ['ParticipantGroup', 'Newsletter'];
const COMPLETION_RULES = ['AllPresenceKnown', 'Manual'];

const enumValue = (names, value, fallback) => {
  if (typeof value === 'number') return names[value] ?? fallback;
  return names.includes(value) ? value : fallback;
};

export const normalizeStepKind = (v) => enumValue(STEP_KINDS, v, 'ManualCheck');
export const normalizeTargetKind = (v) => enumValue(TARGET_KINDS, v, 'ParticipantGroup');
export const normalizeCompletionRule = (v) => enumValue(COMPLETION_RULES, v, 'AllPresenceKnown');

// Kind names/icons, newsletter label and rules are shared with the process page (util/processSteps.js).
export { NEWSLETTER_TARGET_LABEL, STEP_KIND_ICON, STEP_KIND_LABEL, TEMPLATE_RULES };

export const STEP_KIND_HINT = {
  AssignLeaders: 'Spieltagleitung für die Events eintragen. Dieser Schritt hat keine Ziele.',
  Release: 'Macht die Events für die gewählten Gruppen sichtbar und buchbar.',
  SendMail: 'Kündigt die Events per Mail an die gewählten Ziele an.',
  ManualCheck: 'Wird im Prozess nur abgehakt. Dieser Schritt hat keine Ziele.',
};

export const COMPLETION_RULE_LABEL = {
  AllPresenceKnown: 'Abschluss, sobald alle Anwesenheiten erfasst sind',
  Manual: 'Manueller Abschluss',
};

// ── Form model ──────────────────────────────────────────────────────────────────────────────────
// Steps and flat-rate rows carry a client key (stable React key while reordering); the id of existing
// rows is kept and sent back. Prices are entry input strings per group ('' = inherit the group default).
// Mail rules are keyed "slot|groupId" ("slot|all" = all groups); null values inherit. Flat-rate rows
// ("Standard-Pauschalen/-Abzüge") follow the row model of components/flat-rate/FlatRateRowsEditor (no
// amount — a process copies the catalog amount of its location when it is created): ONE list with `kind`
// per row, Pauschalen first, then Abzüge (the tab edits both slices, see splitFlatRateRows). Add-on rows
// (tab "Zusätze") follow the row model of components/add-on/AddOnRowsEditor (mode "template": optional own
// values, empty = catalog at the process location).
let keySeq = 0;
export const nextKey = () => `step${++keySeq}`;

export const mailRuleKey = (slot, participantGroupId) => `${slot}|${participantGroupId ?? 'all'}`;

// Slots the server resolves without a template (the group welcome mail: MemberMailService /
// ParticipantGroupMembershipService ask IMailPolicy with templateId = null) — a template rule would have no
// effect, so the editor neither offers nor keeps them.
export const NON_TEMPLATE_MAIL_SLOTS = new Set(['welcome']);

export function blankForm() {
  return {
    id: null,
    name: '',
    eventLabel: '',
    description: '',
    icon: '',
    colorKey: 'neutral',
    requiresLeader: false,
    neverPublic: false,
    linkSignup: false,
    prepaymentRequired: false,
    autoPaidOnPresenceDefault: true,
    completionRule: 'AllPresenceKnown',
    steps: [],
    prices: {},
    mailRules: {},
    flatRates: [],
    addOns: [],
    isArchived: false,
    usage: null,
  };
}

export function newStep(kind) {
  return {
    key: nextKey(),
    id: null,
    kind,
    label: STEP_KIND_LABEL[kind] ?? '',
    icon: STEP_KIND_ICON[kind] ?? '',
    targets: [],
  };
}

// Flat-rate rows of the form by kind → { flatRates, deductions } (order within a kind kept) and back
// (Pauschalen first, then Abzüge — the order the server stores).
export function splitFlatRateRows(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return { flatRates: list.filter(r => !isDeduction(r)), deductions: list.filter(isDeduction) };
}

export const joinFlatRateRows = ({ flatRates, deductions }) => [...(flatRates ?? []), ...(deductions ?? [])];

// ProcessTemplateResponse → editable form.
export function templateToForm(t) {
  const form = blankForm();
  if (!t) return form;

  const prices = {};
  (t.prices ?? []).forEach(p => {
    prices[Number(p.participantGroupId)] = { entry: toPriceInput(p.entryPrice) };
  });

  const mailRules = {};
  (t.mailRules ?? []).filter(r => !NON_TEMPLATE_MAIL_SLOTS.has(r.slot)).forEach(r => {
    mailRules[mailRuleKey(r.slot, r.participantGroupId)] = {
      enabled: typeof r.enabled === 'boolean' ? r.enabled : null,
      textblockId: r.textblockId ?? null,
    };
  });

  return {
    ...form,
    id: t.id,
    name: t.name ?? '',
    eventLabel: t.eventLabel ?? '',
    description: t.description ?? '',
    icon: t.icon ?? '',
    colorKey: normalizeColorKey(t.colorKey),
    requiresLeader: !!t.requiresLeader,
    neverPublic: !!t.neverPublic,
    linkSignup: !!t.linkSignup,
    prepaymentRequired: !!t.prepaymentRequired,
    autoPaidOnPresenceDefault: !!t.autoPaidOnPresenceDefault,
    completionRule: normalizeCompletionRule(t.completionRule),
    steps: [...(t.steps ?? [])]
      .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
      .map(s => ({
        key: nextKey(),
        id: s.id ?? null,
        kind: normalizeStepKind(s.kind),
        label: s.label ?? '',
        icon: s.icon ?? '',
        targets: (s.targets ?? []).map(x => ({
          targetKind: normalizeTargetKind(x.targetKind),
          participantGroupId: x.participantGroupId != null ? Number(x.participantGroupId) : null,
          textblockId: x.textblockId ?? null,
        })),
      })),
    prices,
    mailRules,
    flatRates: joinFlatRateRows(splitFlatRateRows((t.flatRates ?? []).map(f => {
      const kind = normalizeFlatRateKind(f.kind);
      const deduction = kind === FLAT_RATE_KIND.deduction;
      return {
        key: nextKey(),
        id: f.id ?? null,
        kind,
        flatRateId: f.flatRateId != null ? Number(f.flatRateId) : null,
        role: deduction ? null : normalizeRole(f.role),
        playerId: !deduction && f.playerId != null ? Number(f.playerId) : null,
        amount: '',
        flatRateName: f.flatRateName ?? '',
        recipientName: deduction ? '' : (f.playerName ?? ''),
        catalogAmount: f.amount ?? null, // catalog standard, null = "entfällt"
      };
    }))),
    addOns: (t.addOns ?? []).map(templateAddOnToRow),
    isArchived: !!t.isArchived,
    usage: t.usage ?? null,
  };
}

// Targets as the server stores them: none for AssignLeaders/ManualCheck, groups without textblock for
// Release, groups + newsletter with their textblock for SendMail.
function targetsForRequest(step) {
  switch (step.kind) {
    case 'Release':
      return step.targets
        .filter(t => t.targetKind === 'ParticipantGroup')
        .map(t => ({ targetKind: 'ParticipantGroup', participantGroupId: t.participantGroupId, textblockId: null }));
    case 'SendMail':
      return step.targets.map(t => ({
        targetKind: t.targetKind,
        participantGroupId: t.targetKind === 'ParticipantGroup' ? t.participantGroupId : null,
        textblockId: t.textblockId ?? null,
      }));
    default:
      return [];
  }
}

// Editable form → SaveProcessTemplateRequest. Rows that only inherit are left out (the server drops
// them as well).
export function formToRequest(form) {
  return {
    name: form.name.trim(),
    eventLabel: form.eventLabel.trim(),
    description: form.description.trim() || null,
    icon: form.icon.trim() || null,
    colorKey: normalizeColorKey(form.colorKey),
    requiresLeader: form.requiresLeader,
    neverPublic: form.neverPublic,
    linkSignup: form.linkSignup,
    prepaymentRequired: form.prepaymentRequired,
    autoPaidOnPresenceDefault: form.autoPaidOnPresenceDefault,
    completionRule: form.completionRule,
    steps: form.steps.map((s, i) => ({
      id: s.id ?? null,
      sortOrder: (i + 1) * 10,
      kind: s.kind,
      label: s.label.trim(),
      icon: s.icon.trim() || null,
      targets: targetsForRequest(s),
    })),
    prices: Object.keys(form.prices)
      .map(id => ({ participantGroupId: Number(id), entryPrice: parsePriceInput(form.prices[id].entry) }))
      .filter(p => p.entryPrice != null),
    mailRules: Object.keys(form.mailRules)
      .map(key => {
        const [slot, groupPart] = key.split('|');
        const rule = form.mailRules[key];
        return {
          participantGroupId: groupPart === 'all' ? null : Number(groupPart),
          slot,
          enabled: typeof rule.enabled === 'boolean' ? rule.enabled : null,
          textblockId: rule.textblockId ?? null,
        };
      })
      .filter(r => (r.enabled != null || r.textblockId != null) && !NON_TEMPLATE_MAIL_SLOTS.has(r.slot)),
    // Array order = SortOrder on the server; names and amounts of the response are display-only.
    // Deductions have no recipient.
    flatRates: form.flatRates.map(f => {
      const role = isDeduction(f) ? null : normalizeRole(f.role);
      return {
        id: f.id ?? null,
        flatRateId: f.flatRateId,
        role,
        playerId: role || isDeduction(f) ? null : (f.playerId ?? null),
      };
    }),
    // Array order = display order; empty values = catalog at the process location.
    addOns: addOnRowsToTemplateRequest(form.addOns),
  };
}

// Stable snapshot for "unsaved changes" detection.
export const formSignature = (form) => JSON.stringify(formToRequest(form));

// ── Client-side hints (mirror of the server validator) ─────────────────────────────────────────

// Group may be a release target: the guest group or a group with a personal link (form groups see
// what guests see).
export const isReleaseTargetGroup = (g) => !!g && (g.isGuest || normalizeSignupChannel(g.signupChannel) === 'PersonalLink');

// Problems of one step (texts in the server's wording, without the step prefix).
export function stepIssues(step, rules, groupsById) {
  const issues = [];
  if (!step.label.trim()) issues.push('Bitte eine Bezeichnung angeben.');

  const groupTargets = step.targets.filter(t => t.targetKind === 'ParticipantGroup');
  const hasNewsletter = step.targets.some(t => t.targetKind === 'Newsletter');

  groupTargets.forEach(t => {
    const g = groupsById?.get(Number(t.participantGroupId));
    if (!g) {
      if (groupsById?.size) issues.push('Eine Zielgruppe existiert nicht mehr.');
      return;
    }
    if (g.isArchived) issues.push(`Die Gruppe „${g.name}“ ist archiviert.`);
    if (step.kind === 'Release') {
      if (g.isGuest && rules.neverPublic) {
        issues.push(`Events dieser Vorlage sind nie öffentlich – keine Freigabe für „${g.name}“.`);
      }
      if (!isReleaseTargetGroup(g)) {
        issues.push(`„${g.name}“ meldet über das Formular an und sieht, was Gäste sehen – nur Gruppen mit persönlichem Link oder Gäste sind Freigabe-Ziele.`);
      }
    }
    if (step.kind === 'SendMail' && g.isGuest) {
      issues.push(`„${g.name}“ ist kein Mail-Ziel – Gäste erreichst du über „Newsletter“.`);
    }
  });

  if (step.kind === 'Release' && groupTargets.length === 0) issues.push('Bitte mindestens eine Gruppe freigeben.');
  if (step.kind === 'SendMail') {
    if (step.targets.length === 0) issues.push('Bitte mindestens ein Ziel wählen.');
    if (hasNewsletter && rules.neverPublic) issues.push('Events dieser Vorlage sind nie öffentlich – kein Newsletter.');
    // Group targets may fall back to the group's mail rule, the newsletter has none.
    if (step.targets.some(t => t.targetKind === 'Newsletter' && t.textblockId == null)) {
      issues.push('Bitte für den Newsletter einen Textbaustein wählen.');
    }
  }
  return issues;
}

// Template-level warnings (do not block saving).
export function templateWarnings(form) {
  const warnings = [];
  if (form.requiresLeader && !form.steps.some(s => s.kind === 'AssignLeaders')) {
    warnings.push('Die Vorlage verlangt eine Spieltagleitung, hat aber keinen Schritt „Spieltagleiter“.');
  }
  if (!form.steps.some(s => s.kind === 'Release')) {
    warnings.push('Ohne Freigabe-Schritt werden die Events nur über das Bearbeiten-Popup sichtbar.');
  }
  return warnings;
}

// Invalid price inputs (negative / not a number) — the only check the server cannot report per field.
export function priceIssue(form, groupsById) {
  for (const id of Object.keys(form.prices)) {
    const row = form.prices[id];
    if (Number.isNaN(parsePriceInput(row.entry))) {
      const name = groupsById?.get(Number(id))?.name;
      return name
        ? `Eintritt für „${name}“: bitte 0 oder mehr eingeben oder das Feld leeren.`
        : 'Eintritt: bitte 0 oder mehr eingeben oder das Feld leeren.';
    }
  }
  return '';
}

// Incomplete or duplicate flat-rate / deduction rows (recipient, entry) — the rows show the details;
// unknown catalog entries or persons are reported by the server validator.
export const flatRateIssue = (form) => flatRateRowsIssue(form.flatRates);

// Incomplete, duplicate or invalid add-on rows — the rows show the details; unknown catalog entries are
// reported by the server validator.
export const addOnIssue = (form, groupsById) => addOnRowsIssue(form.addOns, { mode: ADD_ON_ROW_MODE.template, groupsById });

// Material symbol names: lower case, digits and underscores.
export const sanitizeIconName = (value) => String(value ?? '')
  .toLowerCase()
  .replace(/\s+/g, '_')
  .replace(/[^a-z0-9_]/g, '')
  .slice(0, 50);

// "1 Prozess (1 offen)" / "3 Events (2 kommend)" — card + usage tab.
export function usageParts(usage) {
  const u = usage ?? {};
  const processCount = Number(u.processCount ?? 0);
  const openProcessCount = Number(u.openProcessCount ?? 0);
  const eventCount = Number(u.eventCount ?? 0);
  const futureEventCount = Number(u.futureEventCount ?? 0);
  return { processCount, openProcessCount, eventCount, futureEventCount };
}

export function usageSummary(usage) {
  const { processCount, openProcessCount, eventCount, futureEventCount } = usageParts(usage);
  if (processCount === 0 && eventCount === 0) return 'Nicht genutzt';
  const processes = `${processCount} ${processCount === 1 ? 'Prozess' : 'Prozesse'}`
    + (openProcessCount > 0 ? ` (${openProcessCount} offen)` : '');
  const events = `${eventCount} ${eventCount === 1 ? 'Event' : 'Events'}`
    + (futureEventCount > 0 ? ` (${futureEventCount} kommend)` : '');
  return `${processes} · ${events}`;
}

// Axios error → text for the editor/page (the API answers 400/409 with a plain message).
export function apiErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (!err?.response) return 'Keine Verbindung zum Server.';
  if (err.response.status === 404) return 'Die Vorlage existiert nicht mehr.';
  if (err.response.status === 403) return 'Dafür fehlen dir die Rechte.';
  return fallback;
}
